
import {
    AuditEscalationGroupModel,
    AuditModel,
    AuditWorkplaceModel,
    EscalationGroupTypeEnum,
    GroupModel,
    LocationModel,
    PermissionTypeEnum,
    PlanItemModel,
    ProcessTypeEnum,
    TenantUserModel,
    WorkplaceModel,
} from '@/libs/Api';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';
import { auditsStore } from '@/libs/audits/+state/store';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import AuditCreateDialog from '@/libs/audits/components/AuditCreateDialog.vue';
import WorkTaskCreateDialog from '@/libs/work-tasks/components/WorkTaskCreateDialog.vue';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { groupsStore } from '@/libs/groups/+state/store';
import { validate as isValidUUID } from 'uuid';
import { auditWorkplacesStore } from '@/libs/audit-workplaces/+state/store';
import { cloneDeep } from 'lodash';
import IssueSolvingItem from '@/libs/issues-solving/components/IssueSolvingItem.vue';
import activeModeService from '@/libs/common/services/active-mode-service';
import { locationsStore } from '@/libs/locations/+state/store';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { auditEscalationGroupsStore } from '@/libs/audit-escalation-groups/+state/store';

@Component({
    components: {
        IssueSolvingItem,
        AuditCreateDialog,
        WorkTaskCreateDialog,
    },
    computed: {
        ProcessTypeEnum() {
            return ProcessTypeEnum;
        },
    },
})
export default class IssuesSolving extends Page {
    filterActive = true;
    mobileFiltersShow = false;
    pageIndex = 1;
    pageSize = 25;
    pagingChanged = false;
    showAllItems = false;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    @Prop() tenantId!: string;
    @Prop() partyId!: string;
    @Ref() dialog!: AuditCreateDialog;
    @Ref() workTaskCreateDialog!: WorkTaskCreateDialog;

    get canMakeReport(): boolean {
        return this.hasPermissions([
            PermissionTypeEnum.ReportIssueSolving,
            PermissionTypeEnum.ShiftWork,
            PermissionTypeEnum.ShiftWorkLimited,
            PermissionTypeEnum.ShiftManagement,
        ]);
    }

    get headers() {
        return [
            { text: this.$t('planItems.solving.name'), value: 'name', align: 'left', sortable: false },
            { text: this.$i18n.t('audits.categoryName'), value: 'category.name', align: 'left', sortable: false },
            { text: this.$i18n.t('audits.owner'), value: 'owner.user.displayName', align: 'left', sortable: false },
            { text: '', value: 'id', align: 'left', sortable: false },
        ];
    }

    get loading(): boolean {
        return auditsStore.useGetter(CrudGetter.Pending) || auditWorkplacesStore.useGetter(CrudGetter.Pending);
    }

    get audits(): AuditModel[] {
        const activeWorkplace = window.localStorage.ActiveWorkplace;
        let workplaces: string[] = [];
        try {
            if (isValidUUID(activeWorkplace)) {
                workplaces = [activeWorkplace as string];
            }
        } catch (e) {
            // ts-ignore
        }
        try {
            workplaces = JSON.parse(window.localStorage.ActiveWorkplace) as string[];
        } catch (e) {
            // ts-ignore
        }
        let items: AuditModel[] = [...auditsStore.useGetter(CrudGetter.Data)] ?? [];
        const auditWorkplaces: AuditWorkplaceModel[] = [...auditWorkplacesStore.useGetter(CrudGetter.Data)] ?? [];
        if (workplaces && workplaces.length > 0) {
            items = items.filter((x) =>
                auditWorkplaces.filter((w) => w.auditId == x.id!).some((w) => workplaces.contains(w.workplaceId!))
            );
        }
        return items;
    }

    get planItemsLoading(): boolean {
        return planItemsStore.useGetter(CrudGetter.PendingBasic) || auditEscalationGroupsStore.useGetter(CrudGetter.Pending);
    }

    get itemCount(): number {
        return [[...planItemsStore.useGetter(CrudGetter.DataBasic)] ?? []].length;
    }

    get actionPlanItems(): PlanItemModel[] {
        let items = [...planItemsStore.useGetter(CrudGetter.DataBasic)] as PlanItemModel[];
        if (this.showAllItems) {
            return items;
        }
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
        const activeStations = activeModeService.getActiveStations();
        const activeWorkplaces = activeModeService.getActiveWorkplaces();
        const activeLocations = activeModeService.getActiveLocations();
        const locationScopes = locations
            .filter((x) => x.locationParents!.some((y) => activeLocations.includes(y.parentalLocationId!)))
            .map((x) => x.id!)
            .concat(activeLocations!);
        if (activeStations.length > 0) {
            items = items.filter((x) => activeStations.includes(x.stationId!));
        } else if (activeWorkplaces.length > 0) {
            items = items.filter((x) => activeWorkplaces.includes(x.workplaceId!));
        } else if (activeLocations.length > 0) {
            const workplacesToFilter = workplaces.filter((x) => locationScopes.includes(x.locationId!)).map((x) => x.id!);
            items = items.filter((x) => workplacesToFilter.includes(x.workplaceId!));
        }
        return items;
    }

    get auditEscalationGroups(): AuditEscalationGroupModel[] {
        return auditEscalationGroupsStore.useGetter(CrudGetter.Data);
    }

    @Watch('filterActive')
    filtersChanged() {
        this.pageIndex = 1;
        this.load();
    }

    @Watch('pageIndex')
    @Watch('pageSize')
    paginationChanged() {
        this.saveQuery();
        if (this.pagingChanged) {
            this.pagingChanged = false;
            return;
        }
        this.loadActivePlanItems();
    }

    mounted() {
        if (this.getQueryInt('pageSize') != null) {
            this.pageSize = this.getQueryInt('pageSize') ?? 25;
        } else {
            if (window.localStorage.ptPageSize && Number(window.localStorage.ptPageSize)) {
                this.pageSize = Number(window.localStorage.ptPageSize);
            }
            this.saveQuery();
        }
        this.load();
        this.loadActivePlanItems();
        this.loadAuditEscalationGroups();
    }

    load() {
        const payload = this.getPayload();
        auditsStore.dispatch(CrudAction.GetAll, payload);
        const payloadWorkplaces = cloneDeep(payload);
        payloadWorkplaces.query.data.orderby.forEach((e) => {
            e.field = `audit.${e.field}`;
        });
        payloadWorkplaces.query.data.predicates.forEach((e) => {
            e.field = `audit.${e.field}`;
        });
        auditWorkplacesStore.dispatch(CrudAction.GetAll, payloadWorkplaces);
    }

    getFilters(): CrudQueryPredicate[] {
        let filters: CrudQueryPredicate[] = [
            {
                field: 'processType',
                op: 'eq',
                comparand: ProcessTypeEnum.IssueSolving,
            },
        ];
        
        // Nový systém: filtrovat kategorie s kontextovými oprávněními
        if (!this.tenantUser.accessAllCategories) {
            const accessible = this.getUserAccessibleCategoryIds([
                PermissionTypeEnum.PerformAudit,
                PermissionTypeEnum.Audits,
            ]);
            
            if (accessible.length > 0) {
                filters.push({
                    field: 'categoryId',
                    op: 'in',
                    comparand: accessible,
                });
            } else {
                // Žádný přístup = prázdný výsledek
                filters.push({ field: 'id', op: 'eq', comparand: '00000000-0000-0000-0000-000000000000' });
            }
        }
        
        filters.push({ field: 'isActive', op: 'eq', comparand: this.filterActive });
        return filters;
    }

    getPayload() {
        return createCrudQueryPayload<AuditModel>({ field: 'name', index: 1, order: 'asc' }, this.getFilters());
    }

    loadActivePlanItems() {
        const groups: GroupModel[] = groupsStore.useGetter(CrudGetter.Data) ?? [];
        const myGroupIds = groups
            .filter((x) => x.groupUsers?.some((n) => n.tenantUserId == this.tenantUser.id))
            .map((x) => x.id!);
        planItemsStore.dispatch(
            CrudAction.GetAllBasic,
            createCrudQueryPayload(
                { field: 'time', index: 1, order: 'desc' },
                [
                    { field: 'processType', op: 'eq', comparand: ProcessTypeEnum.IssueSolving },
                    { field: 'audit.auditEscalationGroups.groupId', op: 'in', comparand: myGroupIds },
                    {
                        field: 'id',
                        op: 'wrap',
                        comparand: [
                            {
                                field: 'planItemState',
                                op: 'eq',
                                comparand: 'WaitingToPerform',
                                junction: 'or',
                            },
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'isWorkplaceShutdown',
                                        op: 'eq',
                                        comparand: true,
                                    },
                                    {
                                        field: 'shutdownToDate',
                                        op: 'eq',
                                        comparand: null,
                                    },
                                ],
                                junction: 'or',
                            },
                        ],
                    },
                ],
                {
                    pageIndex: 1,
                    pageSize: 999,
                }
            )
        );
    }

    loadAuditEscalationGroups() {
        auditEscalationGroupsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<AuditEscalationGroupModel>(undefined, [
                { field: 'escalationGroupType', op: 'eq', comparand: EscalationGroupTypeEnum.IssueEscalation },
            ])
        );
    }

    saveQuery() {
        this.setQuery('pageSize', this.pageSize?.toString());
        window.localStorage.ptPageSize = this.pageSize?.toString();
    }
}
